/**
 * Lucky Loser display helpers.
 *
 * After a withdrawal is recorded with mode='rearrange', the bottom-of-band
 * `draw_seeding` row is flagged with `is_lucky_loser_slot = true`. The
 * withdrawn player's id sits at that slot in `draw_match` until a Lucky
 * Loser is assigned. While `is_lucky_loser_slot` is true, any renderer
 * showing player names from the draw should substitute the placeholder
 * label "Lucky Loser" (translated) instead of the player's name.
 *
 * When `assignLuckyLoser` runs, it (a) swaps the LL's player_id into the
 * slot and (b) clears `is_lucky_loser_slot`. So the rule for renderers is
 * simple: `is_lucky_loser_slot === true` => placeholder.
 *
 * Keep this module dependency-free so it can be imported from server-side
 * report generators, client components, and worker code alike.
 */

export interface LuckyLoserSeedingRow {
  player_id: string;
  is_lucky_loser_slot?: boolean | null;
}

export interface PlayerLike {
  id: string;
  first_name: string;
  last_name: string;
}

/**
 * Build the set of player_ids that should render as a Lucky Loser
 * placeholder for the given seeding rows.
 */
export function getLuckyLoserPlayerIds(
  seeding: LuckyLoserSeedingRow[] | null | undefined,
): Set<string> {
  const out = new Set<string>();
  for (const s of seeding ?? []) {
    if (s.is_lucky_loser_slot && s.player_id) out.add(s.player_id);
  }
  return out;
}

/**
 * If the given player is at a Lucky Loser slot, return a substituted
 * player record carrying the localized label. Otherwise return the
 * original player unchanged.
 *
 * The substituted record uses an `ll-` prefixed id so consumers can
 * distinguish it from a real player when needed.
 */
export function applyLuckyLoserLabel<P extends PlayerLike>(
  player: P | null | undefined,
  luckyLoserPlayerIds: Set<string> | null | undefined,
  label: string,
): P | null | undefined {
  if (!player || !luckyLoserPlayerIds || !luckyLoserPlayerIds.has(player.id)) {
    return player;
  }
  return {
    ...player,
    id: `ll-${player.id}`,
    first_name: label,
    last_name: '',
  };
}

export function isLuckyLoserPlaceholderId(id: string | null | undefined): boolean {
  return typeof id === 'string' && id.startsWith('ll-');
}
